import { useTranslation } from "react-i18next";
import {
  useCurrentUser,
  reloadCurrentUser,
} from "../../../../domains/user/hooks/useCurrentUser";
import { useStore } from "../../../../lib/store";
import {
  disableHapticStore,
  disableSoundStore,
} from "../../../../domains/user/stores/nativeSettingsStore";
import { apiClient } from "../../../../services/api";
import { usePushNotificationSwitch } from "../../../../domains/notification/hooks/usePushNotificationSwitch";
import { SettingsRow } from "../SettingsRow";
import { SettingsSectionLabel } from "../SettingsSectionLabel";
import { SettingsSubHeader } from "../SettingsSubHeader";

export function NotificationsView(): React.JSX.Element {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const [disableSound, setDisableSound] = useStore(disableSoundStore);
  const [disableHaptic, setDisableHaptic] = useStore(disableHapticStore);
  const push = usePushNotificationSwitch();

  async function toggleEmail(): Promise<void> {
    if (currentUser === null) return;
    try {
      await apiClient.patch("/webapp/private/users/me", {
        json: { emailOptOut: !currentUser.emailOptOut },
      });
      await reloadCurrentUser();
    } catch {
      // API errors are surfaced globally by onRequestError; there is nothing to
      // advance on failure — the toggle simply stays put.
    }
  }

  async function setExperimentalFeatures(on: boolean): Promise<void> {
    try {
      await apiClient.patch("/webapp/private/users/me", {
        json: { experimentalFeatures: on },
      });
      await reloadCurrentUser();
    } catch {
      // API errors are surfaced globally by onRequestError; the switch stays put.
    }
  }

  return (
    <div className="flex h-full flex-col">
      <SettingsSubHeader title={t("Notifications")} />
      <div className="flex-1 overflow-y-auto scroll-fog-y px-5 pb-12">
        <div className="flex flex-col divide-y divide-ds-slate-100">
          <SettingsRow
            icon={null}
            title={
              push.denied ? t("Notifications blocked") : t("Push notifications")
            }
            subtitle={push.isNative ? t("On your device") : null}
            right={{
              kind: "switch",
              checked: push.enabled,
              onCheckedChange: push.toggle,
            }}
            tone="default"
            onClick={null}
          />
          {currentUser !== null ? (
            <SettingsRow
              icon={null}
              title={t("Emails")}
              subtitle={currentUser.email}
              right={{
                kind: "switch",
                checked: !currentUser.emailOptOut,
                onCheckedChange: () => {
                  void toggleEmail();
                },
              }}
              tone="default"
              onClick={null}
            />
          ) : null}
        </div>

        <div className="mt-6 flex flex-col gap-1">
          <SettingsSectionLabel>{t("PREFERENCES")}</SettingsSectionLabel>
          <div className="flex flex-col divide-y divide-ds-slate-100">
            <SettingsRow
              icon={null}
              title={t("Sound effects")}
              subtitle={null}
              right={{
                kind: "switch",
                checked: !disableSound,
                onCheckedChange: (checked) => {
                  setDisableSound(!checked);
                },
              }}
              tone="default"
              onClick={null}
            />
            <SettingsRow
              icon={null}
              title={t("Haptic effects")}
              subtitle={null}
              right={{
                kind: "switch",
                checked: !disableHaptic,
                onCheckedChange: (checked) => {
                  setDisableHaptic(!checked);
                },
              }}
              tone="default"
              onClick={null}
            />
          </div>
        </div>

        {currentUser !== null && currentUser.experimentalFeatures !== null ? (
          <div className="mt-6 flex flex-col gap-1">
            <SettingsSectionLabel>{t("JUISCI STAFF")}</SettingsSectionLabel>
            <div className="flex flex-col divide-y divide-ds-slate-100">
              <SettingsRow
                icon={null}
                title={t("Experimental features")}
                subtitle={t(
                  "Features still in test. They can change or break."
                )}
                right={{
                  kind: "switch",
                  checked: currentUser.experimentalFeatures,
                  onCheckedChange: (checked) => {
                    void setExperimentalFeatures(checked);
                  },
                }}
                tone="default"
                onClick={null}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
